'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { getBuildVersion } from '#pplx/frontend/apps/ask/fetchers/getBuildVersion.js';
import { getMinVersionRefresh, isBelowMinVersionRefresh, } from '#pplx/frontend/apps/ask/fetchers/getMinVersionRefresh.js';
import { useHasActiveVoiceSession } from '#pplx/frontend/apps/ask/stores/voiceSessionStore.js';
import { MAX_DELAY, INACTIVE_THRESHOLD, MIN_VERSION_REFRESH_CHECK_INTERVAL, CHANNEL_NAME, DISPLAY_MANUAL_REFRESH_TIMEOUT, MANUAL_REFRESH_TOAST_DEDUP_KEY, } from '#pplx/frontend/apps/ask/version/RefreshSideEffectsShared.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { reportAction } from '#pplx/frontend/metrics/browser.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { reloadWindow } from '#pplx/frontend/platform-components/reloadWindow.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { clearPersistedCache } from '#pplx/frontend/react-query/queryClient.js';
import { useHasSomeActiveStreams } from '#pplx/frontend/stream/stream.js';
import { clearServiceWorkerRuntimeCache } from '#pplx/frontend/workers/service-worker/utils/clearServiceWorkerRuntimeCache.js';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { useIntl } from 'react-intl';
// 2 days
export const RefreshSideEffects = React.memo(function RefreshSideEffects({ enabled, buildVersion, buildTimestamp, ref }) {
    const hasActiveStreams = useHasSomeActiveStreams();
    const hasActiveVoiceSession = useHasActiveVoiceSession();
    const { env: { isLocalhost }, } = useWebPlatform();
    const currentBuildVersion = useRef(buildVersion);
    const outdatedBuildVersionRef = useRef(false);
    const [reloadAfter, setReloadAfter] = useState(0);
    const forcedRef = useRef(false);
    const forcedReloadDueRef = useRef(false);
    const forcedTimerRef = useRef(undefined);
    const reloadIfHiddenRef = useRef(() => { });
    const broadcastChannelRef = useRef(null);
    broadcastChannelRef.current ??=
        typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
    const outdated = enabled && outdatedBuildVersionRef.current;
    const checkBuildVersion = useCallback(async () => {
        try {
            if (isLocalhost || outdatedBuildVersionRef.current || !enabled) {
                return;
            }
            const latestBuildVersion = await getBuildVersion();
            reportAction('build_version_refresh_check_version', {
                isCometSidecarClient: !!import.meta.env.VITE_IS_SIDECAR_BUILD,
                currentVersion: buildVersion,
                latestVersion: latestBuildVersion,
            });
            if (!buildVersion && latestBuildVersion) {
                currentBuildVersion.current = latestBuildVersion;
                return;
            }
            if (buildVersion && latestBuildVersion && latestBuildVersion !== buildVersion) {
                outdatedBuildVersionRef.current = true;
                setReloadAfter(Date.now() + Math.random() * MAX_DELAY);
            }
        }
        catch {
            logger.log('Error getting build version');
        }
    }, [buildVersion, enabled, isLocalhost]);
    // Forces a reload only when the min-version refresh and `/api/version` agree this build is stale.
    const checkMinVersionRefresh = useCallback(async () => {
        if (import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD ||
            import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
            return;
        }
        if (isLocalhost || !enabled || !buildVersion || forcedRef.current)
            return;
        try {
            const minVersionRefresh = await getMinVersionRefresh();
            if (minVersionRefresh === null ||
                !isBelowMinVersionRefresh(buildTimestamp, minVersionRefresh)) {
                return;
            }
            if (!outdatedBuildVersionRef.current)
                await checkBuildVersion();
            if (!outdatedBuildVersionRef.current || forcedRef.current)
                return;
            forcedRef.current = true;
            forcedTimerRef.current = setTimeout(() => {
                forcedReloadDueRef.current = true;
                reloadIfHiddenRef.current();
            }, Math.random() * minVersionRefresh.reloadWindowMs);
            reportAction('min_version_refresh_forced', {
                currentVersion: buildVersion,
                buildTimestamp,
                minBuildTimestamp: minVersionRefresh.minBuildTimestamp,
            });
        }
        catch {
            logger.log('Error getting min-version refresh');
        }
    }, [buildTimestamp, buildVersion, checkBuildVersion, enabled, isLocalhost]);
    const canRefresh = useCallback(() => {
        if (!outdated ||
            !reloadAfter ||
            document.hidden ||
            hasActiveStreams() ||
            hasActiveVoiceSession() ||
            Date.now() < reloadAfter) {
            return false;
        }
        return true;
    }, [hasActiveStreams, hasActiveVoiceSession, outdated, reloadAfter]);
    const refresh = useCallback(async (url, immediate, onlyIfHidden) => {
        if (!outdated || !reloadAfter)
            return false; // no need to try again
        if (!immediate && !canRefresh())
            return true; // try again later
        if (onlyIfHidden === true && !document.hidden)
            return true;
        try {
            await clearServiceWorkerRuntimeCache();
        }
        catch {
            logger.log('Error clearing service worker runtime cache');
        }
        try {
            await clearPersistedCache();
        }
        catch {
            logger.log('Error clearing persisted cache');
        }
        // The awaits above can outlast the tab staying hidden; a forced reload stays due.
        if (onlyIfHidden === true && !document.hidden)
            return true;
        reportAction('build_version_refresh');
        broadcastChannelRef.current?.postMessage(CHANNEL_NAME);
        if (url) {
            redirectWindow(url, 'Build version refresh (redirect)');
        }
        else {
            reloadWindow('Build version refresh (reload)');
        }
        return false; // success (and appease typescript)
    }, [canRefresh, outdated, reloadAfter]);
    // Forced reloads only happen while hidden; visible tabs keep the normal navigation path.
    useEffect(() => {
        reloadIfHiddenRef.current = () => {
            if (document.hidden)
                void refresh(undefined, true, true);
        };
    });
    const checkVersion = useMemo(() => debounce(checkBuildVersion, INACTIVE_THRESHOLD, {
        maxWait: MAX_DELAY,
        leading: false,
        trailing: true,
    }), [checkBuildVersion]);
    useEffect(() => {
        document.addEventListener('visibilitychange', checkVersion);
        window.addEventListener('focus', checkVersion);
        window.addEventListener('keydown', checkVersion);
        window.addEventListener('mousedown', checkVersion);
        const interval = setInterval(checkBuildVersion, MAX_DELAY);
        const minVersionRefreshInterval = setInterval(checkMinVersionRefresh, MIN_VERSION_REFRESH_CHECK_INTERVAL);
        const reloadIfForcedAndHidden = () => {
            if (forcedReloadDueRef.current)
                reloadIfHiddenRef.current();
        };
        document.addEventListener('visibilitychange', reloadIfForcedAndHidden);
        return () => {
            document.removeEventListener('visibilitychange', checkVersion);
            window.removeEventListener('focus', checkVersion);
            window.removeEventListener('keydown', checkVersion);
            window.removeEventListener('mousedown', checkVersion);
            clearInterval(interval);
            clearInterval(minVersionRefreshInterval);
            document.removeEventListener('visibilitychange', reloadIfForcedAndHidden);
        };
    }, [checkVersion, checkBuildVersion, checkMinVersionRefresh]);
    useEffect(() => {
        const channel = broadcastChannelRef.current;
        return () => {
            if (channel)
                channel.close();
            clearTimeout(forcedTimerRef.current);
        };
    }, []);
    useImperativeHandle(ref, () => ({
        isOutdated: () => outdated,
        canRefresh: () => canRefresh(),
        refresh: async (url) => await refresh(url),
        channel: broadcastChannelRef.current,
    }));
    return (_jsx(RefreshManager, { buildTimestamp: buildTimestamp, canRefresh: canRefresh, channel: broadcastChannelRef.current, outdated: outdated, refresh: refresh }));
});
const RefreshManager = React.memo(function RefreshManager({ buildTimestamp, outdated, refresh, canRefresh, channel, }) {
    const { openToast } = useToastContext();
    const { $t } = useIntl();
    const [refreshImmediately, setRefreshImmediately] = useState(false);
    const refreshToast = useRef(null);
    const refreshManually = outdated && canRefresh() && Date.now() - buildTimestamp >= DISPLAY_MANUAL_REFRESH_TIMEOUT;
    // Set up broadcast channel listener
    useEffect(() => {
        if (import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD ||
            import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD ||
            !channel ||
            !outdated) {
            return;
        }
        const handleMessage = () => setRefreshImmediately(true);
        channel.addEventListener('message', handleMessage);
        return () => channel.removeEventListener('message', handleMessage);
    }, [channel, outdated]);
    // Set up immediate refresh on visible
    useEffect(() => {
        if (!refreshImmediately)
            return;
        const refreshOnVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshToast.current?.closeToast();
                refresh(undefined, true);
            }
        };
        document.addEventListener('visibilitychange', refreshOnVisible);
        return () => document.removeEventListener('visibilitychange', refreshOnVisible);
    }, [refresh, refreshImmediately]);
    // Set up manual refresh toast
    useEffect(() => {
        if (!refreshManually)
            return;
        refreshToast.current = openToast({
            variant: 'neutral',
            iconOverride: $icon('refresh'),
            message: $t({
                defaultMessage: 'Update to latest version', // Original string from IdleRefresh
                id: 'f72854f54d',
                description: 'Toast notification in the Refresh Manager app version section',
            }),
            ctaText: $t({
                defaultMessage: 'Update',
                id: 'b18f7ba2eb',
                description: 'Toast action that refreshes the app to the latest version',
            }),
            ctaOnClick: () => refresh(undefined, true),
            timeoutSeconds: null,
            dedupKey: MANUAL_REFRESH_TOAST_DEDUP_KEY,
        });
        return () => {
            refreshToast.current?.closeToast();
            refreshToast.current = null;
        };
    }, [$t, openToast, refresh, refreshManually]);
    return null;
});
//# sourceMappingURL=RefreshSideEffects.js.map